// /Users/wujiazhen/dream前端/templates/my-app/src/components/ui/hooks/use-field-calculation.jsx

import { useCallback } from 'react';
import { getValueByCode } from '../ot';

/**
 * 欄位計算 Hook
 * 處理表單欄位之間的聯動計算（例如：起訖時間計算時長）
 */
export const useFieldCalculation = () => {

    /**
     * 根據 code 取得欄位
     * @param {Array} elements - 所有元件列表
     * @param {string} code - 欄位的 code (例如: "起", "訖", "名稱")
     * @returns {Object|null} - 找到的元件或 null
     */
    const getElementByCode = useCallback((elements, code) => {
        if (!elements || !code) return null;
        return elements.find(el =>
            el.form_form_column_data?.[0]?.code === code
        );
    }, []);

    /**
     * 根據配置取得值
     * @param {Array} elements - 所有元件列表
     * @param {string} code - 欄位 code
     * @param {string} valueType - 值類型："欄位參考" 或其他
     * @param {*} directValue - 直接值（當不是欄位參考時使用）
     * @returns {*} - 取得的值
     */
    const getValueFromConfig = useCallback((elements, code, valueType, directValue) => {
        if (valueType === "欄位參考") {
            const el = getElementByCode(elements, code);
            return el?.content ?? "";
        } else if (valueType === "欄位帶入") {
            const el = getElementByCode(elements, code);
            return el?.content ?? "";
        }
        return directValue;
    }, [getElementByCode]);

    /**
     * 執行單一計算
     * @param {Array} elements - 所有元件列表
     * @param {Object} calcData - 計算配置資料
     * @returns {string|number} - 計算結果
     */
    const calculateField = useCallback((elements, calcData) => {
        if (!calcData) return "";

        const {
            operation,
            code_major,
            code_minor,
            value_type_major,
            value_type_minor,
            value_major,
            value_minor
        } = calcData;

        // 取得主要值和次要值
        const majorValue = getValueFromConfig(elements, code_major, value_type_major, value_major);
        const minorValue = getValueFromConfig(elements, code_minor, value_type_minor, value_minor);

        // 如果任一值為空，返回空字串
        if (!majorValue && !minorValue) return "";

        // 根據運算類型執行計算
        switch (operation) {
            case "加": {
                const major = Number(majorValue) || 0;
                const minor = Number(minorValue) || 0;
                return (major + minor).toString();
            }

            case "減": {
                // 檢查是否為日期時間類型
                const majorDate = new Date(majorValue);
                const minorDate = new Date(minorValue);

                if (!isNaN(majorDate.getTime()) && !isNaN(minorDate.getTime())) {
                    // 日期時間相減：計算時長（日、小時、分鐘）
                    const diffMs = majorDate.getTime() - minorDate.getTime();
                    const totalMinutes = Math.round(diffMs / (1000 * 60));

                    const days = Math.floor(totalMinutes / (24 * 60));
                    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
                    const minutes = totalMinutes % 60;

                    // 組合成可讀格式
                    const parts = [];
                    if (days > 0) parts.push(`${days} 天`);
                    if (hours > 0) parts.push(`${hours} 小時`);
                    if (minutes > 0) parts.push(`${minutes} 分鐘`);

                    // 如果都是 0，返回 "0 分鐘"
                    return parts.length > 0 ? parts.join(' ') : "0 分鐘";
                }

                // 數值相減
                const major = Number(majorValue) || 0;
                const minor = Number(minorValue) || 0;
                return (major - minor).toString();
            }

            case "乘": {
                const major = Number(majorValue) || 0;
                const minor = Number(minorValue) || 0;
                return (major * minor).toString();
            }

            case "除": {
                const major = Number(majorValue) || 0;
                const minor = Number(minorValue) || 1; // 避免除以零
                if (minor === 0) return "錯誤：除數不能為零";
                return (major / minor).toString();
            }

            case "百分比": {
                const major = Number(majorValue) || 0;
                const minor = Number(minorValue) || 1;
                if (minor === 0) return "0%";
                return `${((major / minor) * 100).toFixed(2)}%`;
            }

            default:
                return "";
        }
    }, [getValueFromConfig]);

    const referField = useCallback((elements, calcData, getItem) => {
        if (!calcData) return "";

        const {
            operation,
            code_major,
            code_minor,
            value_type_major,
            value_type_minor,
            value_major,
            value_minor
        } = calcData;

        const filter = undefined;
        const majorValue = getValueFromConfig(elements, code_major, value_type_major, value_major);
        const doc = calcData?.calculate_document_data?.find(_ => true)
        const response_data = getItem(doc?.document_id, filter)?.response_data
        const minorRefer = response_data?.find(item => item?.response_id == majorValue)
        const minorValue = getValueByCode({
            code: code_minor,
            response_form_value_data: minorRefer?.response_form_value_data
        })
        return minorValue
        // if (!response_data) {
        //     response_data = getOrFetchItem(doc?.document_id, filter)?.response_data
        // }
        // if (!response_data) return ""

    }, [getValueFromConfig]);

    const requireField = useCallback((elements, requiredData, getItem) => {
        if (!requiredData) return "";

        let is_element_required = false
        const {
            code,
            code_major,
            code_minor,
            condition,
            value_type,
            value,
            value_type_major,
            value_type_minor,
            value_major,
            value_minor
        } = requiredData;

        if(value_type === "固定值") {
            const majorValue = getValueFromConfig(elements, code_major, value_type_major, value_minor);
            if(majorValue && majorValue > 0) is_element_required = true
        }
        return is_element_required

    }, [getValueFromConfig]);

    /**
     * 遞迴計算所有依賴欄位
     * 支援連鎖計算：A → B → C
     * @param {Array} elements - 元件列表
     * @param {number} maxDepth - 最大遞迴深度（防止無限循環）
     * @returns {Array} - 更新後的元件列表
     */
    const recalculateAll = useCallback((elements, maxDepth = 10, getItem, changeElementCode) => {
        if (maxDepth <= 0) {
            console.warn('達到最大計算深度，可能存在循環依賴');
            return elements;
        }

        let hasChanges = false;

        const result = elements.map(el => {

            /* ---------------------------------------------------------------------------------------------------- */
            /* 必填與非必填的判斷 */
            const requiredData = el.form_form_column_data?.[0]?.form_column_requirement_data?.[0];
            if (requiredData) {
                let requiredValue = requireField(elements, requiredData)
                el = {
                    ...el,
                    form_form_column_data: el?.form_form_column_data?.map(e => {
                        return {
                            ...e,
                            is_required: requiredValue ? "1" : "0"
                        }
                    })
                }
                console.log({
                    "el": el,
                    "requiredData": requiredData,
                    "requiredValue": requiredValue
                })
            }
            /* ---------------------------------------------------------------------------------------------------- */

            // 檢查是否有計算配置
            const calcData = el.form_form_column_data?.[0]?.form_column_calculate_data?.[0];
            if (["欄位引用"].includes(el.form_form_column_data?.[0]?.default_value_type)) return el;
            if (!calcData) return el;

            let newValue = null;
            if (["欄位帶入"].includes(el.form_form_column_data?.[0]?.default_value_type)) {
                if (changeElementCode && changeElementCode === calcData?.code_major) {
                    newValue = referField(elements, calcData, getItem);
                }
                else{
                    newValue = el?.content
                }
            }
            else {
                newValue = calculateField(elements, calcData);
            }

            // 計算新值
            // 只在值真的改變時才更新
            if (el.content !== newValue) {
                hasChanges = true;
                return { ...el, content: newValue };
            }

            return el;
        });

        // 如果有變化，繼續遞迴計算（處理連鎖依賴）
        return hasChanges ? recalculateAll(result, maxDepth - 1, getItem, changeElementCode) : result;
    }, [calculateField, referField]);

    /**
     * 帶計算的更新函數
     * @param {Object} element - 要更新的元件
     * @param {*} newValue - 新值
     * @param {Array} allElements - 當前所有元件
     * @returns {Array} - 更新並重新計算後的元件列表
     */
    const updateWithCalculation = useCallback((element, newValue, allElements, getItem) => {
        // 第一步：更新當前欄位
        const updatedElements = allElements.map(el =>
            el.id === element.id ? { ...el, content: newValue } : el
        );
        let form_form_column_data_first = element?.form_form_column_data?.find(_ => true)
        let code = form_form_column_data_first?.code;

        // 第二步：遞迴計算所有依賴欄位
        return recalculateAll(updatedElements, undefined, getItem, code);
    }, [recalculateAll]);

    /**
     * 檢查欄位是否依賴其他欄位
     * @param {Object} element - 要檢查的元件
     * @returns {Object|null} - 依賴資訊或 null
     */
    const getFieldDependencies = useCallback((element) => {
        const calcData = element?.form_form_column_data?.[0]?.form_column_calculate_data?.[0];
        if (!calcData) return null;

        return {
            operation: calcData.operation,
            dependsOn: [
                calcData.value_type_major === "欄位參考" ? calcData.code_major : null,
                calcData.value_type_minor === "欄位參考" ? calcData.code_minor : null,
            ].filter(Boolean)
        };
    }, []);

    return {
        updateWithCalculation,
        calculateField,
        recalculateAll,
        getElementByCode,
        getFieldDependencies,
    };
};

export default useFieldCalculation;